import { useEffect, useRef, useState } from 'react';
import { Loader2, Mic, Square, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { setBusy } from '@/lib/busy';
import { startTranscribeSession } from '@/lib/api/capture';
import {
  releaseMicrophoneStream,
  requestMicrophoneStream,
  startLiveTranscription,
  type LiveTranscriptChunk,
  type LiveTranscriptionSession,
} from '@/lib/voice';

type Status = 'idle' | 'requesting' | 'connecting' | 'recording' | 'saving';

export interface LiveTranscriptBoxProps {
  /** Called with the operator's final text once they press Save, and the
   *  total time the mic was live across every take. Registers as a voice
   *  note with a `transcript` and no `audioKey` (CAPTURE_JOBS.md section
   *  3). Never called with empty text. */
  onSave: (transcript: string, durationMs: number) => void | Promise<void>;
  languageCode?: string;
  className?: string;
}

function joinWords(a: string, b: string): string {
  return [a.trim(), b.trim()].filter(Boolean).join(' ');
}

const STATUS_LINE: Partial<Record<Status, string>> = {
  requesting: 'Starting the microphone…',
  connecting: 'Connecting…',
  recording: 'Listening…',
  saving: 'Saving…',
};

/**
 * The live voice control on the viewfinder and the review screen
 * (CAPTURE_JOBS.md sections 3 and 6). The mic streams to Amazon
 * Transcribe over the presigned websocket from `startTranscribeSession`
 * (`lib/voice/stream.ts`), and partial then final text appears live in
 * an editable box below it. The operator can edit the text at any time
 * between takes, press the mic again to append more, or clear it.
 *
 * Exported for the viewfinder to mount at the start of a capture (a later
 * wave); used here in `VoiceNotesPanel` in place of the batch recorder
 * for `NOTE`s. This component owns no capture id: the caller's `onSave`
 * decides how the text is persisted, immediately or through the offline
 * queue.
 */
export function LiveTranscriptBox({ onSave, languageCode, className }: LiveTranscriptBoxProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const sessionRef = useRef<LiveTranscriptionSession | null>(null);
  const baseTextRef = useRef('');
  const textRef = useRef('');
  textRef.current = text;
  const elapsedMsRef = useRef(0);
  const startedAtRef = useRef(0);

  function commitElapsed() {
    if (startedAtRef.current) {
      elapsedMsRef.current += Date.now() - startedAtRef.current;
      startedAtRef.current = 0;
    }
  }

  function teardown() {
    commitElapsed();
    sessionRef.current?.stop();
    sessionRef.current = null;
    if (streamRef.current) {
      releaseMicrophoneStream(streamRef.current);
      streamRef.current = null;
    }
  }

  // A live mic stream and an open socket are both real resources: leaving
  // this screen mid-recording releases both rather than leaving the
  // browser's recording indicator lit and the socket billing for nothing.
  // Inlined rather than calling `teardown` (refs only, so this never needs
  // to rerun) to avoid redeclaring the cleanup on every render.
  useEffect(() => {
    return () => {
      sessionRef.current?.stop();
      if (streamRef.current) releaseMicrophoneStream(streamRef.current);
    };
  }, []);

  function handleChunk(chunk: LiveTranscriptChunk) {
    if (chunk.isPartial) {
      setText(joinWords(baseTextRef.current, chunk.text));
      return;
    }
    baseTextRef.current = joinWords(baseTextRef.current, chunk.text);
    setText(baseTextRef.current);
  }

  async function start() {
    setError(null);
    setStatus('requesting');
    let stream: MediaStream;
    try {
      stream = await requestMicrophoneStream();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not start the microphone.');
      setStatus('idle');
      return;
    }
    streamRef.current = stream;

    setStatus('connecting');
    try {
      const session = await startTranscribeSession(languageCode ? { languageCode } : {});
      baseTextRef.current = textRef.current;
      sessionRef.current = startLiveTranscription({
        url: session.url,
        stream,
        onChunk: handleChunk,
        onError: (message) => {
          setError(message);
          teardown();
          setStatus('idle');
        },
      });
      startedAtRef.current = Date.now();
      setStatus('recording');
    } catch {
      teardown();
      setError('Could not connect to live transcription. Check your connection and try again.');
      setStatus('idle');
    }
  }

  function stop() {
    teardown();
    setStatus('idle');
  }

  function handleMicClick() {
    if (status === 'recording') {
      stop();
    } else if (status === 'idle') {
      void start();
    }
  }

  function handleClear() {
    setText('');
    baseTextRef.current = '';
    elapsedMsRef.current = 0;
    setError(null);
  }

  async function handleSave() {
    const trimmed = text.trim();
    if (!trimmed) return;
    setStatus('saving');
    try {
      await onSave(trimmed, elapsedMsRef.current);
      setText('');
      baseTextRef.current = '';
      elapsedMsRef.current = 0;
      setStatus('idle');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save the voice note.');
      setStatus('idle');
    }
  }

  // A recording in progress is unsaved work: the deployment watcher must not
  // reload over it (plan/portal-final/INTERFACES.md section 8).
  useEffect(() => {
    setBusy('voice', status !== 'idle');
  }, [status]);
  useEffect(() => () => setBusy('voice', false), []);

  const busy = status === 'requesting' || status === 'connecting' || status === 'saving';
  const recording = status === 'recording';
  const statusText = STATUS_LINE[status];

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-center gap-2">
        {/* 44px target: the mic sits at arm's length from an operator
            standing at the item, same as the batch recorder's button. */}
        <Button
          type="button"
          size="icon"
          variant={recording ? 'destructive' : 'outline'}
          disabled={busy}
          aria-label={recording ? 'Stop recording' : text ? 'Record more' : 'Record a voice note'}
          aria-pressed={recording}
          onClick={handleMicClick}
        >
          {busy ? (
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          ) : recording ? (
            <Square className="size-4" aria-hidden="true" />
          ) : (
            <Mic className="size-4" aria-hidden="true" />
          )}
        </Button>
        {statusText && (
          <p className="text-xs text-muted" aria-live="polite">
            {statusText}
          </p>
        )}
      </div>

      <textarea
        aria-label="Voice note transcript"
        readOnly={recording || busy}
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="Transcript"
        rows={4}
        className="w-full rounded-control border border-border bg-transparent px-3 py-2 text-sm text-surface-foreground placeholder:text-muted focus-visible:outline-none focus-visible:border-brand disabled:cursor-not-allowed disabled:opacity-50"
      />

      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!text || recording || busy}
          onClick={handleClear}
        >
          <X className="size-4" aria-hidden="true" />
          Clear
        </Button>
        <Button type="button" size="sm" disabled={!text.trim() || recording || busy} onClick={() => void handleSave()}>
          {status === 'saving' ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : 'Save'}
        </Button>
      </div>
    </div>
  );
}
